<template>
  <div>
    <label :for="id" class="tw-block tw-font-bold">
      {{ label }}
    </label>
    <div class="tw-relative tw-mt-1 tw-rounded-md">
      <div
        class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-border-r tw-border-bridgit-navy-25 tw-px-4"
      >
        <span class="tw-font-medium">$</span>
      </div>
      <input
        :id="id"
        type="text"
        class="tw-block tw-w-full tw-flex-1 tw-rounded-md tw-border-bridgit-navy-25 tw-py-2 tw-pl-14 tw-pr-3 focus:tw-border-bridgit-lightBlue focus:tw-ring-bridgit-lightBlue"
        :value="data.inputValue"
        :placeholder="placeholder"
        inputmode="numeric"
        autocomplete="off"
        @beforeinput="handleBeforeInput"
        @input="handleInput"
        @focusout="handleFocusOut"
      />
    </div>
    <input
      id="newHomeValueRange"
      tabindex="-1"
      type="range"
      :min="sliderMin"
      :max="sliderMax"
      :value="data.sliderValue"
      class="tw-mt-3 tw-w-full tw-accent-bridgit-green"
      @input="handleSliderInput"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue';
import { useNumberInput } from '@/helpers/numberInput';
import { debounce } from '@/helpers/common';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Number,
    default: undefined,
  },
  label: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  sliderMin: {
    type: Number,
    default: 0,
  },
  sliderMax: {
    type: Number,
    default: 5000000,
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | undefined): void;
}>();

const { parseValue, formatValue, handleBeforeInput } = useNumberInput();

const data = reactive({
  numberValue: props.modelValue,
  inputValue: formatValue(props.modelValue),
  sliderValue: props.modelValue ?? props.sliderMin,
});

let numberValueChanged = false;
let inputValueChanged = false;

watch(
  () => props.modelValue,
  (value) => {
    // note: use Object.is to allow comparison of NaN values
    if (!Object.is(value, data.numberValue)) {
      data.numberValue = value;
      data.inputValue = formatValue(value);
      data.sliderValue = value ?? props.sliderMin;
      numberValueChanged = false;
      inputValueChanged = false;
    }
  },
);

function handleInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  if (value === data.inputValue) {
    return;
  }

  data.inputValue = value;
  inputValueChanged = true;

  const newNumberValue = value ? parseValue(value, true) : undefined;
  // note: use Object.is to allow comparison of NaN values
  if (!Object.is(newNumberValue, data.numberValue)) {
    data.numberValue = newNumberValue;
    numberValueChanged = true;
  }
}

const debouncedUpdateEvent = debounce(() => {
  emit('update:modelValue', data.numberValue);
  numberValueChanged = false;
}, 500);
function handleSliderInput(event: Event) {
  const value = (event.target as HTMLInputElement).valueAsNumber;
  data.sliderValue = Math.round(value / 1000) * 1000;

  data.numberValue = data.sliderValue;
  numberValueChanged = true;

  data.inputValue = formatValue(data.sliderValue);
  inputValueChanged = false;

  debouncedUpdateEvent();
}

function handleFocusOut() {
  if (numberValueChanged) {
    data.sliderValue = data.numberValue ?? props.sliderMin;
    emit('update:modelValue', data.numberValue);
    numberValueChanged = false;
  }
  // apply formatting unless NaN
  if (inputValueChanged && (data.numberValue === undefined || !isNaN(data.numberValue))) {
    data.inputValue = formatValue(data.numberValue);
    inputValueChanged = false;
  }
}
</script>

<style scoped lang="scss">
input[type='range'] {
  appearance: none;
}
input[type='range']:focus {
  outline: none;
}
@mixin slider-thumb {
  width: 24px;
  height: 24px;
  border-radius: 24px;
  background: #fff;
  border: 5px solid theme('colors.bridgit.vibrantGreen.DEFAULT');
  margin-top: -6px;
}
@mixin slider-thumb-hover {
  border-width: 4px;
}
@mixin slider-track {
  height: 12px;
  border-radius: 12px;
  background: theme('colors.gray.200');
  cursor: pointer;
}
input[type='range']::-webkit-slider-thumb {
  appearance: none;
  @include slider-thumb;
}
input[type='range']::-moz-range-thumb {
  @include slider-thumb;
}
input[type='range']:hover::-webkit-slider-thumb {
  @include slider-thumb-hover;
}
input[type='range']:hover::-moz-range-thumb {
  @include slider-thumb-hover;
}
input[type='range']::-webkit-slider-runnable-track {
  @include slider-track;
}
input[type='range']::-moz-range-track {
  @include slider-track;
}
</style>
